import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

const FRIENDLY_MAP: Array<{ test: string; message: string }> = [
  { test: 'invalid login credentials', message: 'E-posta veya şifre hatalı. Lütfen tekrar deneyin.' },
  { test: 'email not confirmed', message: 'E-posta adresiniz henüz onaylanmamış. Lütfen e-posta kutunuzu kontrol ederek onay bağlantısına tıklayın.' },
  { test: 'user already registered', message: 'Bu e-posta ile daha önce hesap oluşturulmuş. Giriş yapmayı deneyin veya şifrenizi sıfırlayın.' },
  { test: 'already been registered', message: 'Bu e-posta ile daha önce hesap oluşturulmuş. Giriş yapmayı deneyin veya şifrenizi sıfırlayın.' },
  { test: 'duplicate', message: 'Bu e-posta ile daha önce hesap oluşturulmuş. Giriş yapmayı deneyin veya şifrenizi sıfırlayın.' },
  { test: 'password', message: 'Şifre çok zayıf. En az 6 karakterli, güçlü bir şifre belirleyin.' },
  { test: 'weak', message: 'Şifre çok zayıf. En az 6 karakterli, güçlü bir şifre belirleyin.' },
  { test: 'rate limit', message: 'Çok fazla deneme yaptınız. Lütfen biraz bekleyip tekrar deneyin.' },
  { test: 'too many', message: 'Çok fazla deneme yaptınız. Lütfen biraz bekleyip tekrar deneyin.' },
  { test: 'network', message: 'Sunucu bağlantısı kurulamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.' },
  { test: 'fetch failed', message: 'Sunucu bağlantısı kurulamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.' },
  { test: 'timeout', message: 'Sunucu bağlantısı kurulamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.' },
  { test: 'forbidden', message: 'Bu işlem için yetkiniz bulunmuyor.' },
  { test: 'expired', message: 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.' },
  { test: 'token', message: 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.' },
];

function getFriendlyError(message: string): string {
  const lower = message.toLowerCase();
  for (const entry of FRIENDLY_MAP) {
    if (lower.includes(entry.test)) {
      return entry.message;
    }
  }
  return message;
}

export default function LoginPage() {
  const navigate = useNavigate();

  // ---- Giriş formu state ----
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // ---- Şifremi unuttum state ----
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [clientReady, setClientReady] = useState(false);

  const hasSupabase = Boolean(supabaseUrl && supabaseAnonKey && supabase && supabase.auth);

  // Mevcut session varsa direkt yönlendir
  useEffect(() => {
    if (!supabase || !supabase.auth) {
      setClientReady(true);
      return;
    }

    supabase.auth.getSession()
      .then((result) => {
        if (result?.data?.session) {
          navigate('/', { replace: true });
        }
        setClientReady(true);
      })
      .catch((err: any) => {
        // Refresh token geçersizse veya session yoksa yerel session'ı temizle
        const errMsg = err?.message || '';
        if (errMsg.includes('Refresh Token') || errMsg.includes('token') || errMsg.includes('session missing') || errMsg.includes('Auth session')) {
          supabase?.auth.signOut({ scope: 'local' }).catch(() => {});
        }
        setClientReady(true);
      });
  }, [navigate]);

  // Mod değiştirmede tüm hata/mesajları sıfırla
  const switchMode = (newMode: 'login' | 'forgot') => {
    setLoginError('');
    setForgotError('');
    setForgotMessage('');
    setMode(newMode);
  };

  // ---- Giriş ----
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!hasSupabase) {
      setLoginError('Supabase bağlantısı yapılandırılmamış.');
      return;
    }
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setLoginError('E-posta ve şifre gereklidir.');
      return;
    }

    setLoginLoading(true);
    try {
      const { error: authError } = await supabase!.auth.signInWithPassword({
        email: loginEmail.trim(),
        password: loginPassword,
      });

      if (authError) {
        setLoginError(getFriendlyError(authError.message));
      } else {
        navigate('/', { replace: true });
      }
    } catch {
      setLoginError('Giriş yapılırken beklenmedik bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoginLoading(false);
    }
  };

  // ---- Şifremi unuttum ----
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');

    if (!hasSupabase) {
      setForgotError('Supabase bağlantısı yapılandırılmamış.');
      return;
    }
    if (!forgotEmail.trim()) {
      setForgotError('E-posta adresinizi giriniz.');
      return;
    }

    setForgotLoading(true);
    try {
      const { error: resetError } = await supabase!.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: window.location.origin + '/login',
      });

      if (resetError) {
        setForgotError(getFriendlyError(resetError.message));
      } else {
        setForgotMessage('Şifre sıfırlama bağlantısı e-posta adresinize gönderildi. Lütfen gelen kutunuzu kontrol edin.');
      }
    } catch {
      setForgotError('Şifre sıfırlama sırasında beklenmedik bir hata oluştu.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (!clientReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl overflow-hidden animate-pulse">
            <img
              src="https://storage.readdy-site.link/project_files/3da75122-8c1c-4e05-bb8b-0bbdfa25568f/77be6b44-7bba-43ed-a1ae-cb71d6f2b97a_polatlar-appico.jpg"
              alt="Polatlar Mobilya"
              className="w-full h-full object-contain"
            />
          </div>
          <p className="text-sm text-foreground-400 font-sans">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const isForgot = mode === 'forgot';
  const title = isForgot ? 'Şifremi Unuttum' : 'Giriş Yap';
  const subtitle = isForgot
    ? 'E-posta adresinize şifre sıfırlama bağlantısı gönderelim'
    : 'Yönetim paneline erişmek için giriş yapın';

  return (
    <div className="min-h-screen flex bg-background-50">
      {/* Sol Panel - Marka */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary-500 relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/40 to-primary-950/60" />
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-background-50/20" />
          <div className="absolute bottom-32 right-16 w-96 h-96 rounded-full bg-accent-500/20" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-background-50/5" />
        </div>
        <div className="relative z-10 text-center px-12">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl overflow-hidden bg-black">
            <img
              src="https://storage.readdy-site.link/project_files/3da75122-8c1c-4e05-bb8b-0bbdfa25568f/77be6b44-7bba-43ed-a1ae-cb71d6f2b97a_polatlar-appico.jpg"
              alt="Polatlar Mobilya"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-background-50 font-heading mb-4">Polatlar Mobilya</h1>
          <p className="text-lg text-background-50/80 font-sans leading-relaxed max-w-md mx-auto">
            Müşteri, satış, stok ve ödeme yönetiminizi tek panelden kontrol edin. İşletmenizin tüm operasyonlarını güvenle yönetin.
          </p>
          <div className="mt-10 flex items-center justify-center gap-6 text-sm text-background-50/60">
            <span className="flex items-center gap-2">
              <i className="ri-shield-check-line text-lg" />
              Güvenli
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-flashlight-line text-lg" />
              Hızlı
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-smartphone-line text-lg" />
              Her Cihazda
            </span>
          </div>
        </div>
      </div>

      {/* Sağ Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8">
        <div className="w-full max-w-md">
          {/* Mobil logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl overflow-hidden bg-black">
              <img
                src="https://storage.readdy-site.link/project_files/3da75122-8c1c-4e05-bb8b-0bbdfa25568f/77be6b44-7bba-43ed-a1ae-cb71d6f2b97a_polatlar-appico.jpg"
                alt="Polatlar Mobilya"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 font-heading">Polatlar Mobilya</h1>
            <p className="text-sm text-foreground-400 mt-1">Yönetim Paneli</p>
          </div>

          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8">
            <h2 className="text-xl font-semibold text-foreground-900 font-heading mb-1">{title}</h2>
            <p className="text-sm text-foreground-400 mb-6">{subtitle}</p>

            {/* ===== GİRİŞ FORMU ===== */}
            {mode === 'login' && (
              <>
                {loginError && (
                  <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2.5">
                    <i className="ri-error-warning-line text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-amber-800">{loginError}</p>
                      {loginError.includes('E-posta veya şifre hatalı') && (
                        <p className="text-xs text-amber-600 mt-1.5">
                          Hesabınız yoksa{' '}
                          <button type="button" onClick={() => navigate('/signup')} className="underline font-medium hover:text-amber-800 transition-colors cursor-pointer">
                            Kayıt Ol
                          </button>
                          {' '}sayfasından işletmenizi oluşturabilirsiniz.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label htmlFor="login-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      E-posta
                    </label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
                      <input
                        id="login-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="ornek@polatlar.com"
                        className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="login-password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Şifre
                    </label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
                      <input
                        id="login-password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                        autoComplete="current-password"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap hover:bg-primary-600 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loginLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        Giriş yapılıyor...
                      </span>
                    ) : (
                      'Giriş Yap'
                    )}
                  </button>
                </form>

                <div className="mt-5 text-center space-y-2">
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
                  >
                    Şifremi Unuttum
                  </button>
                  <div className="mt-4 pt-4 border-t border-background-200">
                    <p className="text-xs text-foreground-400 mb-2">Hesabınız yok mu?</p>
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-100 text-accent-700 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap hover:bg-accent-200"
                    >
                      <i className="ri-user-add-line" />
                      İşletmeni Kaydet
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ===== ŞİFREMİ UNUTTUM FORMU ===== */}
            {mode === 'forgot' && (
              <>
                {forgotError && (
                  <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2.5">
                    <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-700">{forgotError}</p>
                  </div>
                )}

                {forgotMessage && (
                  <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-start gap-2.5">
                    <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-emerald-700">{forgotMessage}</p>
                  </div>
                )}

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      E-posta
                    </label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
                      <input
                        id="forgot-email"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="ornek@polatlar.com"
                        className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all"
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap hover:bg-primary-600 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {forgotLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        Gönderiliyor...
                      </span>
                    ) : (
                      'Şifre Sıfırlama Bağlantısı Gönder'
                    )}
                  </button>
                </form>

                <div className="mt-5 text-center">
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
                  >
                    Giriş sayfasına dön
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}